import { Box } from '@mui/material';
import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
} from '@react-awesome-query-builder/mui';

import { AutocompleteResult, AutocompleteResultWithTooltip, TemplatePlaceholder } from '@/types';
import { TemplatableCallingStatusDateRange } from '@pages/QuickFilterListCreatorPage/criteriaComponents/CallingStatusDateRangePicker';
import CallingStatusForm from '@pages/QuickFilterListCreatorPage/criteriaComponents/CallingStatusForm';
import { ListRecordResult } from '@/types/filterCriteriaRecords';
import { MappingError } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';
import { AutocompleteGenericEntity } from '@australiangreens/ag-internal-components';

import { useAdvancedSearchDateFilterInvalid } from './useAdvancedSearchDateFilterInvalid';

export type CallingStatusWidgetValue = {
  callingstatuses: AutocompleteResult[] | TemplatePlaceholder;
  callingstatusinclusion: boolean;
  callingstatusDateRange: TemplatableCallingStatusDateRange;
  callingstatusLists: ListRecordResult[] | TemplatePlaceholder;
  callingstatusUsers: AutocompleteGenericEntity[] | TemplatePlaceholder;
  callingstatusCampaigns: AutocompleteResultWithTooltip[] | TemplatePlaceholder;
  errors: MappingError[];
};

type CombinedProps = BaseWidgetProps<Config, CallingStatusWidgetValue>;

const defaultFilterValue: CallingStatusWidgetValue = {
  callingstatusinclusion: true,
  callingstatuses: [],
  callingstatusDateRange: {
    displayedStartDate: null,
    displayedEndDate: null,
    callingPeriodValue: 'withinlast',
    callingPeriodDetails: 'weeks',
    callingPeriodQuantity: 3,
  },
  callingstatusLists: [],
  callingstatusUsers: [],
  callingstatusCampaigns: [],
  errors: [],
};

const CallingStatusWidgetFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
}: CombinedProps) => {
  const setIsFilterInvalid = useAdvancedSearchDateFilterInvalid();
  const handleChangeCallingStatus = (newValue: AutocompleteResult[] | TemplatePlaceholder) => {
    setValue({
      ...(value ?? defaultFilterValue),
      callingstatuses: newValue,
    });
  };

  const handleChangeInclusion = (newValue: boolean | undefined) => {
    setValue({
      ...(value ?? defaultFilterValue),
      callingstatusinclusion: newValue as boolean,
    });
  };

  const handleChangeDateChange = (newValue: TemplatableCallingStatusDateRange) => {
    setValue({ ...(value ?? defaultFilterValue), callingstatusDateRange: newValue });
  };

  const handleChangeCallingStatusLists = (newValue: ListRecordResult[] | TemplatePlaceholder) => {
    setValue({
      ...(value ?? defaultFilterValue),
      callingstatusLists: newValue,
      errors: value?.errors.filter(({ field }) => field !== 'lists') ?? [],
    });
  };

  const handleChangeCallingStatusUsers = (
    newValue: AutocompleteGenericEntity[] | TemplatePlaceholder
  ) => {
    setValue({
      ...(value ?? defaultFilterValue),
      callingstatusUsers: newValue,
      errors: value?.errors.filter(({ field }) => field !== 'users') ?? [],
    });
  };

  const handleChangeCallingStatusCampaigns = (
    newValue: AutocompleteResultWithTooltip[] | TemplatePlaceholder
  ) => {
    setValue({
      ...(value ?? defaultFilterValue),
      callingstatusCampaigns: newValue,
      errors: value?.errors.filter(({ field }) => field !== 'campaigns') ?? [],
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: "column",
        gap: 3
      }}>
      <CallingStatusForm
        callingStatusValue={value?.callingstatuses ?? []}
        onCallingStatusChange={handleChangeCallingStatus}
        callingStatusInclude={value?.callingstatusinclusion ?? false}
        setCallingStatusInclude={(payload) => handleChangeInclusion(payload)}
        callingStatusDateValue={
          value?.callingstatusDateRange ?? defaultFilterValue.callingstatusDateRange
        }
        onCallingStatusDateChange={handleChangeDateChange}
        callingStatusLists={value?.callingstatusLists ?? []}
        onCallingStatusListsChange={handleChangeCallingStatusLists}
        callingStatusUsers={value?.callingstatusUsers ?? []}
        onCallingStatusUsersChange={handleChangeCallingStatusUsers}
        callingStatusCampaigns={value?.callingstatusCampaigns ?? []}
        onCallingStatusCampaignsChange={handleChangeCallingStatusCampaigns}
        setIsFilterInvalid={setIsFilterInvalid}
        errors={value?.errors}
        isAdvancedSearch
      />
    </Box>
  );
};

export const callingStatusWidget: BaseWidget = {
  type: 'callingStatus',
  hideOperator: true,
  operatorInlineLabel: 'is one of',
  factory: CallingStatusWidgetFactory,
};
